import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCheck, Trash2, AlertTriangle, MessageSquare, Star, FileText, Info, Calendar } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { Notification, NotificationType } from '../types/notification';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import { useLanguage } from '../hooks/useLanguage';

export const NotificationPopup: React.FC = () => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        refreshNotifications
    } = useNotifications();
    const popupRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [showNotifications, setShowNotifications] = useState(() => {
        return localStorage.getItem('showNotifications') !== 'false'; // Default to true
    });

    // Get date locale
    const getDateLocale = () => {
        switch (currentLanguage) {
            case 'en': return enUS;
            case 'ar': return ar;
            default: return fr;
        }
    };

    // Update if preference changes
    useEffect(() => {
        const handleStorageChange = () => {
            setShowNotifications(localStorage.getItem('showNotifications') !== 'false');
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // If notifications are disabled, show nothing
    if (!showNotifications) {
        return null;
    }

    const togglePopup = () => {
        // If opening popup, refresh notifications
        if (!isOpen) {
            refreshNotifications();
        }
        setIsOpen(!isOpen);
    };

    const handleNotificationClick = async (notification: Notification) => {
        // Mark notification as read
        if (!notification.read) {
            await markAsRead(notification._id);
        }

        // If a link is specified, navigate to this link
        if (notification.link) {
            navigate(notification.link);
        }

        // Close popup
        setIsOpen(false);
    };

    const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
        e.stopPropagation(); // Avoid triggering notification click
        await deleteNotification(notificationId);
    };

    const handleMarkAllAsRead = async () => {
        await markAllAsRead();
    };

    const handleClearAll = async () => {
        if (window.confirm(t('notifications.confirmDeleteAll'))) {
            await clearAllNotifications();
        }
    };

    // Get icon based on notification type
    const getNotificationIcon = (type: NotificationType) => {
        switch (type) {
            case 'NEW_INSPECTION':
                return <FileText className="h-5 w-5 text-blue-500" />;
            case 'URGENT_INSPECTION':
                return <AlertTriangle className="h-5 w-5 text-red-500" />;
            case 'NEW_MESSAGE':
                return <MessageSquare className="h-5 w-5 text-green-500" />;
            case 'NEW_REVIEW':
                return <Star className="h-5 w-5 text-yellow-500" />;
            case 'INSPECTION_STATUS_CHANGE':
                return <Info className="h-5 w-5 text-purple-500" />;
            case 'APPOINTMENT':
                return <Calendar className="h-5 w-5 text-indigo-500" />;
            case 'SYSTEM':
            default:
                return <Info className="h-5 w-5 text-gray-500" />;
        }
    };

    return (
        <div className="relative" ref={popupRef}>
            {/* Bell button with badge */}
            <button
                className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={togglePopup}
                aria-label={t('notifications.ariaLabel')}
            >
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                    <span className={`absolute top-0 flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full ${isRTL ? 'left-0' : 'right-0'}`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Notification popup */}
            {isOpen && (
                <div className={`absolute mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl z-50 overflow-hidden ${isRTL ? 'left-0' : 'right-0'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                    {/* Popup header */}
                    <div className={`px-4 py-3 bg-gray-50 border-b flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <h3 className="text-sm font-medium text-gray-700">{t('notifications.title')}</h3>
                        <div className={`flex space-x-1 ${isRTL ? 'space-x-reverse' : ''}`}>
                            <button
                                onClick={handleMarkAllAsRead}
                                className={`p-1 text-xs text-gray-500 hover:text-indigo-600 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                                title={t('notifications.markAllRead')}
                            >
                                <CheckCheck className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
                            </button>
                            <button
                                onClick={handleClearAll}
                                className={`p-1 text-xs text-gray-500 hover:text-red-600 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                                title={t('notifications.deleteAll')}
                            >
                                <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                <span className="hidden sm:inline">{t('notifications.deleteAll')}</span>
                            </button>
                        </div>
                    </div>

                    {/* Notification list */}
                    <div className="max-h-[70vh] overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                {t('notifications.noNotifications')}
                            </div>
                        ) : (
                            <ul>
                                {notifications.map((notification) => (
                                    <li
                                        key={notification._id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`px-4 py-3 border-b hover:bg-gray-50 cursor-pointer ${
                                            !notification.read ? 'bg-blue-50' : ''
                                        } transition-colors duration-200 ease-in-out`}
                                    >
                                        <div className={`flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <div className={`flex-shrink-0 mt-0.5 ${isRTL ? 'ml-3' : ''}`}>
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className={`flex-1 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                                                <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                    <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                                        {notification.title}
                                                    </p>
                                                    <button
                                                        onClick={(e) => handleDeleteNotification(e, notification._id)}
                                                        className={`text-gray-400 hover:text-red-500 ${isRTL ? 'mr-2' : 'ml-2'}`}
                                                        title={t('notifications.delete')}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <p className={`text-xs ${!notification.read ? 'text-gray-700' : 'text-gray-500'}`}>
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {formatDistanceToNow(new Date(notification.createdAt), {
                                                        addSuffix: true,
                                                        locale: getDateLocale()
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};